"use client";

import {
  NotificationPreferenceUpdatePayload,
  getUserNotificationData,
} from "@/data/notification-preferences";
import { updateNotificationPreferences } from "@/data/v2/client/user/notification-preferences";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { Database } from "@punchup/data/supabase.types";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { toast } from "@/shadcn/ui/use-toast";

interface ContactInfo {
  hasEmail: boolean;
  hasPhone: boolean;
  email?: string | null;
  phone?: string | null;
}

interface NotificationPreferencesContextType {
  // Contact Information
  contactInfo: ContactInfo;
  isLoadingContactInfo: boolean;

  // Update Counters (for triggering re-renders)
  phoneUpdateCounter: number;
  emailUpdateCounter: number;

  // Notification Preferences
  smsOptInStatus: boolean;
  emailPreferences: {
    newsletter_email_frequency: string;
  } | null;

  // Actions
  refreshContactInfo: () => Promise<void>;
  triggerPhoneUpdate: () => void;
  triggerEmailUpdate: () => void;
  updateSmsOptIn: (optedIn: boolean) => Promise<void>;
  updateEmailPreferences: (
    preferences: Partial<NotificationPreferenceUpdatePayload>
  ) => Promise<void>;

  // Utility functions
  canReceiveSms: () => boolean;
  canReceiveEmail: () => boolean;
  canReceiveAllNotifications: () => boolean;
}

const NotificationPreferencesContext = createContext<
  NotificationPreferencesContextType | undefined
>(undefined);

// Centralized error handler
const handleError = (error: any, message: string) => {
  console.error(`Error ${message}:`, error);
  toast({
    title: "Error",
    description: `Failed to ${message}`,
    variant: "destructive",
  });
};

export function NotificationPreferencesProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    hasEmail: false,
    hasPhone: false,
  });
  const [isLoadingContactInfo, setIsLoadingContactInfo] = useState(true);
  const [phoneUpdateCounter, setPhoneUpdateCounter] = useState(0);
  const [emailUpdateCounter, setEmailUpdateCounter] = useState(0);
  const [smsOptInStatus, setSmsOptInStatus] = useState(false);
  const [emailPreferences, setEmailPreferences] = useState<{
    newsletter_email_frequency: string;
  } | null>(null);

  const supabase = createClientComponentClient<Database>();

  // Fetch contact info and preferences
  const refreshContactInfo = useCallback(async () => {
    try {
      setIsLoadingContactInfo(true);
      const notificationData = await getUserNotificationData(supabase);

      if (!notificationData) {
        // Reset all state for unauthenticated users
        setContactInfo({ hasEmail: false, hasPhone: false });
        setSmsOptInStatus(false);
        setEmailPreferences(null);
        return;
      }
      const { contactInfo, smsOptInStatus, emailPreferences } =
        notificationData;

      // Update all state at once
      setContactInfo(contactInfo);
      setSmsOptInStatus(smsOptInStatus);
      setEmailPreferences(emailPreferences);
    } catch (error) {
      handleError(error, "load notification preferences");
    } finally {
      setIsLoadingContactInfo(false);
    }
  }, [supabase]);

  // Trigger updates with refresh
  const triggerPhoneUpdate = useCallback(() => {
    setPhoneUpdateCounter((prev) => prev + 1);
    refreshContactInfo();
  }, [refreshContactInfo]);

  const triggerEmailUpdate = useCallback(() => {
    setEmailUpdateCounter((prev) => prev + 1);
    refreshContactInfo();
  }, [refreshContactInfo]);

  // Update SMS opt-in status with optimistic updates
  const updateSmsOptIn = useCallback(
    async (optedIn: boolean) => {
      const previousStatus = smsOptInStatus;

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session?.user?.id) {
          throw new Error("No user session found");
        }

        // Optimistically update UI
        setSmsOptInStatus(optedIn);

        const { error } = await supabase
          .from("notification_preferences_alt")
          .update({ has_opted_in_sms_custom_messaging: optedIn })
          .eq("user_id", session.user.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: `SMS notifications ${optedIn ? "enabled" : "disabled"}`,
        });
      } catch (error) {
        // Revert optimistic update on failure
        setSmsOptInStatus(previousStatus);
        handleError(error, "update SMS preferences");
      }
    },
    [supabase, smsOptInStatus]
  );

  // Update email preferences with optimistic updates
  const updateEmailPreferences = useCallback(
    async (preferences: Partial<NotificationPreferenceUpdatePayload>) => {
      const previousEmailPrefs = emailPreferences;

      try {
        // Optimistically update UI
        if (preferences.newsletter_email_frequency) {
          setEmailPreferences((prev) =>
            prev
              ? {
                  ...prev,
                  newsletter_email_frequency:
                    preferences.newsletter_email_frequency!,
                }
              : {
                  newsletter_email_frequency:
                    preferences.newsletter_email_frequency!,
                }
          );
        }

        const result = await updateNotificationPreferences(preferences);

        if (!result) {
          throw new Error("Failed to update email preferences");
        }

        toast({
          title: "Success",
          description: "Email preferences updated",
        });
      } catch (error) {
        // Revert optimistic update on failure
        setEmailPreferences(previousEmailPrefs);
        handleError(error, "update email preferences");
      }
    },
    [supabase, emailPreferences]
  );

  // Utility functions - memoized for performance
  const canReceiveSms = useCallback(
    () => contactInfo.hasPhone && smsOptInStatus,
    [contactInfo.hasPhone, smsOptInStatus]
  );
  const canReceiveEmail = useCallback(
    () => contactInfo.hasEmail,
    [contactInfo.hasEmail]
  );
  const canReceiveAllNotifications = useCallback(
    () => canReceiveSms() && canReceiveEmail(),
    [canReceiveSms, canReceiveEmail]
  );

  // Initial data fetch
  useEffect(() => {
    refreshContactInfo();
  }, [refreshContactInfo]);

  const value: NotificationPreferencesContextType = {
    contactInfo,
    isLoadingContactInfo,
    phoneUpdateCounter,
    emailUpdateCounter,
    smsOptInStatus,
    emailPreferences,
    refreshContactInfo,
    triggerPhoneUpdate,
    triggerEmailUpdate,
    updateSmsOptIn,
    updateEmailPreferences,
    canReceiveSms,
    canReceiveEmail,
    canReceiveAllNotifications,
  };

  return (
    <NotificationPreferencesContext.Provider value={value}>
      {children}
    </NotificationPreferencesContext.Provider>
  );
}

export function useNotificationPreferences() {
  const context = useContext(NotificationPreferencesContext);
  if (context === undefined) {
    throw new Error(
      "useNotificationPreferences must be used within a NotificationPreferencesProvider"
    );
  }
  return context;
}
